import React, { useEffect, useRef, useState } from "react";

import PanelTemplate from "../../templates/Panel";
import Box from "../../components/Box";
import { Loading } from "../../components/Loading";
import InputColor from "../../components/InputColor";
import PageFooter from "../../components/PageFooter";
import Button from "../../components/Button";
import Modal from "../../components/Modal";
import api from "../../services/api";
import { toast } from "react-toastify";
import { renderError } from "../../helpers/errors";
import { InputSeoComponent } from "../../components/InputSeo";
import { hasPlanTheme, setTheme } from "../../services/auth";
import PlanFeatureUpgradeModal from "../../components/PlanFeatureUpgradeModal";
import { WHATSAPP_THEME_SERVER_URL } from "../../helpers/constants";
import SiteContent from "./SiteContent";

const MainTheme = ({ view = 'identity' }) => {
    const [loading, setLoading] = useState(false);
    const showIdentity = view === 'identity';
    const showTheme = view === 'theme';
    const showSeo = view === 'seo';

    const [vercelProjectId, setVercelProjectId] = useState('');
    const [selectedThemeId, setSelectedThemeId] = useState('');
    const [themes, setThemes] = useState([]);
    const [lockedTheme, setLockedTheme] = useState(null);
    const [themeServerModal, setThemeServerModal] = useState(false);
    const [checkingThemeId, setCheckingThemeId] = useState(null);
    const [pendingTheme, setPendingTheme] = useState(null);
    const [nome, setNome] = useState('');

    const siteContentRef = useRef(null);


    const [corPrimaria, setCorPrimaria] = useState('');
    const [corSecundaria, setCorSecundaria] = useState('');
    const [corTerciaria, setCorTerciaria] = useState('');

    const [logoPngUrl, setLogoPngUrl] = useState(null);
    const [logoPdfUrl, setLogoPdfUrl] = useState(null);
    const [faviconUrl, setFaviconUrl] = useState(null);

    const [seoHomeTitle, setSeoHomeTitle] = useState('');
    const [seoHomeDescription, setSeoHomeDescription] = useState('');
    const [seoHomeOgTitle, setSeoHomeOgTitle] = useState('');
    const [seoHomeOgDescription, setSeoHomeOgDescription] = useState('');
    const [seoHomeOgImageUrl, setSeoHomeOgImageUrl] = useState(null);
    const [seoHomeOgImageRemoved, setSeoHomeOgImageRemoved] = useState(false);
    const [seoHomeOgUrl, setSeoHomeOgUrl] = useState('');

    useEffect(() => {
        getData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const normalizeThemes = data => {
        if(Array.isArray(data)) return data;
        if(Array.isArray(data?.resource)) return data.resource;
        if(Array.isArray(data?.resources)) return data.resources;
        if(Array.isArray(data?.themes)) return data.themes;
        if(Array.isArray(data?.data)) return data.data;
        return [];
    }

    const removeLogo = () => {
        setLogoPngUrl(null);
    }

    const removeLogoPdf = () => {
        setLogoPdfUrl(null);
    }

    const removeFavicon = () => {
        setFaviconUrl(null);
    }

    const removeSeoHomeImage = () =>{
        setSeoHomeOgImageUrl(null);
        setSeoHomeOgImageRemoved(true);
        const eleSeoHomeOgImage = document.querySelector('input[name="seo-home-og-image"]');
        if(eleSeoHomeOgImage) eleSeoHomeOgImage.value = null;
    }

    const getData = () => {
        setLoading(true);

        api.get(`/theme`).then(res => {
            fillTenant(res.data.resource);
            setThemes(normalizeThemes(res.data.themes));
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    const getTenant = () => {
        setLoading(true);
        api.get(`/theme`).then(res => {
            fillTenant(res.data.resource);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    const fillTenant = resource => {
        setNome(resource.nome);
        setCorPrimaria(resource.cor_primaria);
        setCorSecundaria(resource.cor_secundaria);
        setCorTerciaria(resource.cor_terciaria);
        setVercelProjectId(resource.vercel_project_id);
        setSelectedThemeId(resource.theme_id ?? resource.theme?.id ?? '');
        setLogoPngUrl(resource.logo_png ?? null);
        setLogoPdfUrl(resource.logo_pdf_png ?? null);
        setFaviconUrl(resource.favicon ?? null);

        const seoHome = resource?.seo_home ?? null;
        setSeoHomeTitle(seoHome?.title ?? '');
        setSeoHomeDescription(seoHome?.description ?? '');
        setSeoHomeOgTitle(seoHome?.og?.title ?? '');
        setSeoHomeOgDescription(seoHome?.og?.description ?? '');
        setSeoHomeOgImageUrl(seoHome?.og?.image ?? null);
        setSeoHomeOgImageRemoved(false);
        setSeoHomeOgUrl(seoHome?.og?.url ?? '');
    }

    const getSelectedThemeSlug = () => {
        const selected = themes.find(theme => isCurrentTheme(theme));

        return selected?.slug ?? null;
    }

    const getThemeName = theme => theme.name ?? theme.nome ?? 'Tema';

    const getThemeDescription = theme => theme.description ?? theme.descricao ?? 'Tema disponível para o site.';

    const getThemeImage = theme => theme.image ?? theme.image_url ?? theme.imagem ?? theme.thumbnail ?? theme.thumbnail_url ?? theme.preview_image ?? null;

    const getThemePreviewUrl = theme => theme.preview_url ?? theme.demo_url ?? theme.url ?? theme.site_url ?? null;

    const getThemeProjectId = theme => theme.vercel_project_id ?? theme.project_id ?? theme.value ?? '';

    const getThemeSelectionKey = theme => String(theme.id ?? getThemeProjectId(theme) ?? '');

    const isCurrentTheme = theme => {
        const themeProjectId = getThemeProjectId(theme);

        return (
            String(selectedThemeId) !== '' && String(theme.id) === String(selectedThemeId)
        ) || (
            String(vercelProjectId) !== '' && String(themeProjectId) === String(vercelProjectId)
        );
    }

    const chooseTheme = async theme => {
        if (isCurrentTheme(theme)) return;

        if (!hasPlanTheme(theme.id)) {
            setLockedTheme(theme);
            return;
        }

        const themeKey = getThemeSelectionKey(theme);
        setCheckingThemeId(themeKey);

        try {
            // o site em dev roda em localhost, que nunca aponta para o nosso servidor
            const skipServerCheck = process.env.NODE_ENV === 'development';
            const res = skipServerCheck ? null : await api.get('/domain-settings/site-server-status');

            if (res && !res.data?.is_our_server) {
                setThemeServerModal(true);
                return;
            }

            setPendingTheme(theme);
        } catch (error) {
            renderError(error);
        } finally {
            setCheckingThemeId(null);
        }
    }

    const confirmTheme = () => {
        setSelectedThemeId(pendingTheme.id ?? '');
        setVercelProjectId(getThemeProjectId(pendingTheme));
        setPendingTheme(null);
    }

    const openPreview = theme => {
        const previewUrl = getThemePreviewUrl(theme);
        if(!previewUrl) return;
        window.open(previewUrl, '_blank', 'noopener,noreferrer');
    }

    const save = request => {
        setLoading(true);

        request.then(() => {
            toast.success('Dados atualizados com sucesso');
            getTenant();
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    /**
     * Identidade da marca: cores e arquivos. Cada tela envia só os seus campos
     * (BDM-1132) — mandar tudo fazia o backend reprovar a identidade por campos
     * de SEO que nem aparecem aqui.
     */
    const submitIdentity = () => {
        let form = new FormData();

        let fileLogo = document.querySelector('input[name="logo"]')?.files?.[0];

        if(fileLogo !== undefined){
            form.append('logo_png', fileLogo);
        }

        let fileLogoPdf = document.querySelector('input[name="logo-pdf"]')?.files?.[0];

        if(fileLogoPdf !== undefined){
            form.append('logo_pdf_png', fileLogoPdf);
        }

        let fileFavicon = document.querySelector('input[name="favicon"]')?.files?.[0];

        if(fileFavicon !== undefined){
            form.append('favicon', fileFavicon);
        }

        form.append('_method', 'PUT');
        form.append('cor_primaria', corPrimaria);
        form.append('cor_secundaria', corSecundaria);
        form.append('cor_terciaria', corTerciaria);

        save(api.post(`/theme/identity`, form));
    }

    /**
     * Tema do site: sem arquivo no payload, então vai como PUT normal.
     */
    const submitSite = () => {
        if(!vercelProjectId){
            toast.error('Escolha um tema para o site');
            return;
        }

        // O bloco de textos vive dentro do `{!loading && ...}`, então o loading
        // do `save` o desmonta e leva o ref junto. Sem guardar o `save` dos
        // textos aqui, o `?.` engole a gravação e a tela diz "salvo" sem ter salvo.
        const saveSiteContent = siteContentRef.current?.save;

        setTheme(vercelProjectId);

        save(api.put(`/theme/site`, { vercel_project_id: vercelProjectId }).then(() => saveSiteContent?.()));
    }

    const submitSeo = () => {
        if(!seoHomeTitle || seoHomeTitle.trim() === ''){
            toast.error('Título da página (SEO da Home) é obrigatório');
            return;
        }

        if(!seoHomeDescription || seoHomeDescription.trim() === ''){
            toast.error('Descrição da página (SEO da Home) é obrigatória');
            return;
        }

        if(!seoHomeOgTitle || seoHomeOgTitle.trim() === ''){
            toast.error('Título redes sociais (SEO da Home) é obrigatório');
            return;
        }

        if(!seoHomeOgDescription || seoHomeOgDescription.trim() === ''){
            toast.error('Descrição redes sociais (SEO da Home) é obrigatória');
            return;
        }

        let form = new FormData();

        let fileSeoImage = document.querySelector('input[name="seo-home-og-image"]')?.files?.[0];

        if(fileSeoImage !== undefined){
            form.append('seo_home_og_image', fileSeoImage);
        } else if(seoHomeOgImageRemoved){
            form.append('seo_home_og_image_remove', '1');
        }

        form.append('_method', 'PUT');
        form.append('seo_home_title', seoHomeTitle);
        form.append('seo_home_description', seoHomeDescription);
        form.append('seo_home_og_title', seoHomeOgTitle);
        form.append('seo_home_og_description', seoHomeOgDescription);

        save(api.post(`/theme/seo`, form));
    }

    const updateTenant = () => {
        if(showTheme) return submitSite();
        if(showSeo) return submitSeo();

        return submitIdentity();
    }

    const seoGoogle = [
    {
        label: "Título da página",
        value: seoHomeTitle,
        onChange: setSeoHomeTitle,
        placeholder: "Ex: Brindes Personalizados em São Paulo | Minha Empresa",
        maxLength: 60,
        wrapperClass: "seo-home"
    },
    {
        label: "Descrição da página",
        value: seoHomeDescription,
        onChange: setSeoHomeDescription,
        placeholder: "Brindes promocionais personalizados com entrega rápida...",
        maxLength: 160,
        wrapperClass: "seo-home"
    }
];

const seoSocials = [
    {
        label: "Título redes sociais",
        value: seoHomeOgTitle,
        onChange: setSeoHomeOgTitle,
        placeholder: "Título Open Graph",
        maxLength: 60,
        wrapperClass: "seo-redes-sociais"
    },
    {
        label: "Descrição redes sociais",
        value: seoHomeOgDescription,
        onChange: setSeoHomeOgDescription,
        placeholder: "Confira nossos brindes personalizados...",
        maxLength: 160,
        wrapperClass: "seo-redes-sociais"
    }
]
    
    return (
        <>
            <PanelTemplate  id="tenant-edit">
                <Box>
                    {!loading &&
                        <>
                            {showTheme &&
                            <section id="tenant-theme">
                                <div className="theme-header">
                                    <div>
                                        <h2 className="section-title">Tema do site</h2>
                                        <p>Escolha o visual principal do site.</p>
                                    </div>
                                    <span>{themes.length} {themes.length === 1 ? 'tema disponível' : 'temas disponíveis'}</span>
                                </div>

                                {themes.length > 0 &&
                                    <div className="theme-grid">
                                        {themes.map((theme, index) => {
                                            const themeImage = getThemeImage(theme);
                                            const previewUrl = getThemePreviewUrl(theme);
                                            const current = isCurrentTheme(theme);
                                            const themeName = getThemeName(theme);
                                            const locked = !hasPlanTheme(theme.id);
                                            const checkingTheme = checkingThemeId === getThemeSelectionKey(theme);

                                            return (
                                                <article key={theme.id ?? getThemeProjectId(theme) ?? index} className={`theme-card ${current ? 'current' : ''} ${locked ? 'locked' : ''}`}>
                                                    <div className="theme-image">
                                                        {themeImage &&
                                                            <img src={themeImage} alt={themeName} />
                                                        }
                                                        {!themeImage &&
                                                            <div className="theme-image-placeholder">
                                                                <span>{themeName.substring(0, 1)}</span>
                                                            </div>
                                                        }
                                                        {current &&
                                                            <strong>Tema atual</strong>
                                                        }
                                                    </div>
                                                    <div className="theme-body">
                                                        <div>
                                                            <h3>{themeName}</h3>
                                                            <p>{getThemeDescription(theme)}</p>
                                                        </div>
                                                        <div className="theme-actions">
                                                            <button type="button" className="theme-preview-button" onClick={() => openPreview(theme)} disabled={!previewUrl}>
                                                                Ver prévia
                                                            </button>
                                                            {locked && !current ?
                                                                <button type="button" className="theme-plan-button" onClick={() => setLockedTheme(theme)}>
                                                                    <span aria-hidden="true">🔒</span>
                                                                    Upgrade
                                                                </button>
                                                            :
                                                                <button type="button" className="theme-select-button" onClick={() => chooseTheme(theme)} disabled={current || checkingThemeId !== null}>
                                                                    {current ? 'Selecionado' : checkingTheme ? 'Verificando...' : 'Escolher'}
                                                                </button>
                                                            }
                                                        </div>
                                                    </div>
                                                </article>
                                            )
                                        })}
                                    </div>
                                }

                                {themes.length === 0 &&
                                    <div className="theme-empty">
                                        Nenhum tema disponível no momento.
                                    </div>
                                }

                                <SiteContent ref={siteContentRef} themeSlug={getSelectedThemeSlug()} />
                            </section>
                            }

                            {showIdentity &&
                            <section id="tenant-identity">
                                <h2 className="section-title">Identidade da marca</h2>

                                <div className="row">
                                    {/* <div className="col-3">
                                        <InputSelect label={`Tema`} value={vercelProjectId} options={projectsOptions} change={setVercelProjectId} />
                                    </div> */}
                                    <div className="col-3">
                                        <InputColor label={`Cor primária`} value={corPrimaria} change={setCorPrimaria} />
                                    </div>
                                    <div className="col-3">
                                        <InputColor label={`Cor secundária`} value={corSecundaria} change={setCorSecundaria} />
                                    </div>
                                    <div className="col-3">
                                        <InputColor label={`Cor terciária`} value={corTerciaria} change={setCorTerciaria} />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-3">
                                        <h2 className="section-title">Logo no site</h2>

                                        {logoPngUrl &&
                                            <div className="tenant-logo">
                                                <img src={`${logoPngUrl}?t=${new Date().getTime()}`} alt={nome} />
                                                <button onClick={removeLogo}>x</button>
                                            </div>
                                        }
                                        <input style={{ marginBottom: 15, display: logoPngUrl ? 'none' : 'block'}} accept="image/png" type="file" name="logo" />
                                        {!logoPngUrl &&
                                            <p style={{ fontSize: 12}}><b>Formato aceito: PNG. Tamanho ideal: 500px de largura</b></p>
                                        }
                                    </div>

                                    <div className="col-3">
                                        <h2 className="section-title">Favicon no site</h2>

                                        {faviconUrl &&
                                            <div className="tenant-favicon">
                                                <img src={`${faviconUrl}?t=${new Date().getTime()}`} alt={nome} />
                                                <button onClick={removeFavicon}>x</button>
                                            </div>
                                        }
                                        <input style={{ marginBottom: 15, display: faviconUrl ? 'none' : 'block'}} accept="image/png" type="file" name="favicon" />
                                        {!faviconUrl &&
                                            <p style={{ fontSize: 12}}><b>Formato aceito: PNG. Tamanho ideal: 32px por 32px</b></p>
                                        }
                                    </div>

                                    <div className="col-3">
                                        <h2 className="section-title">Logo no PDF, e-mails e login</h2>

                                        {logoPdfUrl &&
                                            <div className="tenant-logo">
                                                <img src={`${logoPdfUrl}?t=${new Date().getTime()}`} alt={nome} />
                                                <button onClick={removeLogoPdf}>x</button>
                                            </div>
                                        }
                                        <input style={{ marginBottom: 15, display: logoPdfUrl ? 'none' : 'block'}} accept="image/png" type="file" name="logo-pdf" />
                                        {!logoPdfUrl &&
                                            <p style={{ fontSize: 12}}><b>Formato aceito: PNG. Tamanho ideal: 500px de largura</b></p>
                                        }
                                    </div>
                                </div>
                            </section>
                            }
                        </>
                        }
                    {loading &&
                        <Loading />
                    }

                    {!loading && showSeo &&
                    <section id="tenant-seo">
                        <h2 className="section-title">SEO e compartilhamento</h2>

                        <div className="container-seo-inputs">
                            <div className="seo-google">
                                <h3>Configurações de SEO para buscadores</h3>
                                <InputSeoComponent fields={seoGoogle}/>
                                <div className="seo-preview">
                                    <h4>Prévia Google:</h4>
                                    <p style={{ fontSize: 12, color: "#666", marginTop: -8, marginBottom: 10 }}>
                                        Imagem de referência: Favicon
                                    </p>
                                    <div className="google-preview">
                                        <div className="google-titles">
                                            {faviconUrl && (
                                                <img
                                                    className="google-favicon-preview"
                                                    src={`${faviconUrl}?t=${new Date().getTime()}`}
                                                    alt={nome}
                                                />
                                            )}
                                            <div className="google-urls">
                                            <div className="url-clean">{seoHomeOgUrl.replace('https://', '').replace('http://', '') || 'https://seudominio.com/'}</div>
                                            <div className="url">{seoHomeOgUrl || 'https://seudominio.com/'}</div>
                                            </div>
                                        </div>
                                        <div className="title">{seoHomeTitle || 'Título da página'}</div>
                                        <div className="desc">{seoHomeDescription || 'Descrição da página'}</div>
                                    </div>

                                </div>
                            </div>
                            <div className="seo-socials">
                                <h3>Configurações de SEO para redes sociais</h3>
                                <InputSeoComponent fields={seoSocials} />
                                <div className="image-input">
                                    <label>Imagem Destaque</label>
                                    {seoHomeOgImageUrl && 
                                        <div className="tenant-og-image">
                                            <img src={`${seoHomeOgImageUrl}`} alt="Imagem OG" width={100} height={100}/>
                                            <button onClick={removeSeoHomeImage}>X</button>
                                        </div>
                                    }
                                    <input 
                                    type="file" 
                                    accept="image/png, image/jpeg" 
                                    name="seo-home-og-image"
                                    onChange={(e) => { 
                                        const file = e.target.files[0];
                                        if (!file) return;
                                        const allowedTypes = ["image/png", "image/jpeg"];
                                        if (!allowedTypes.includes(file.type)) {
                                            alert("Apenas PNG ou JPEG são permitidos");
                                            return;
                                        }

                                        const maxSize = 2 * 1024 * 1024;
                                        if (file.size > maxSize) {
                                            alert("A imagem deve ter no máximo 2MB");
                                            return;
                                        }

                                        const previewUrl = URL.createObjectURL(file);
                                        setSeoHomeOgImageUrl(previewUrl);
                                        setSeoHomeOgImageRemoved(false);
                                    }}
                                   />
                                   {!seoHomeOgImageUrl && 
                                    <p style={{fontSize:12, fontWeight:"bold"}}>Formatos aceitos: JPG ou PNG. Recomendado 1200x630</p>
                                   }
                                </div>

                                <div className="seo-preview">
                                    <h4>Prévia redes sociais</h4>
                                    <div className="social-preview">
                                        <div className="img">
                                            {seoHomeOgImageUrl ? <img src={seoHomeOgImageUrl} alt="Imagem OG" /> : 
                                            <div className="placeholder">Sem imagem</div> }
                                        </div>
                                        <div className="body">
                                            <div className="host">{(seoHomeOgUrl || '').replace(/^https?:\/\//, '').replace(/\/$/, '') || 'seudominio.com'}</div>
                                            <div className="title-socials">{seoHomeOgTitle || 'Título'}</div>
                                            <div className="description-socials">{seoHomeOgDescription || 'Descrição'}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    }
                    

                </Box>
<PlanFeatureUpgradeModal
                    feature="theme"
                    show={lockedTheme !== null}
                    close={() => setLockedTheme(null)}
                />
                <Modal
                    title="Troca de tema indisponível"
                    show={themeServerModal}
                    close={() => setThemeServerModal(false)}
                    footer={
                        <>
                            <Button type="secondary" text="Fechar" action={() => setThemeServerModal(false)} />
                            <Button type="primary" text="Falar no WhatsApp" action={() => window.open(WHATSAPP_THEME_SERVER_URL, '_blank')} />
                        </>
                    }
                >
                    <div className="theme-change-warning">
                        <span className="warning-icon">!</span>
                        <h2>Ajuste seu domínio para continuar</h2>
                        <p>
                            Para trocar o tema, será necessário ajustar a zona DNS do seu domínio. Nosso time pode ajudar você com essa configuração pelo WhatsApp.
                        </p>
                    </div>
                </Modal>
                <Modal
                    title="Trocar tema"
                    show={pendingTheme !== null}
                    close={() => setPendingTheme(null)}
                    footer={
                        <>
                            <Button type="secondary" text="Cancelar" action={() => setPendingTheme(null)} />
                            <Button type="primary" text="Trocar tema" action={confirmTheme} />
                        </>
                    }
                >
                    <div className="theme-change-warning">
                        <span className="warning-icon">!</span>
                        <h2>Seus banners ficam guardados</h2>
                        <p>
                            Banners de formatos que o tema {pendingTheme ? getThemeName(pendingTheme) : ''} não tem deixam de aparecer no site. Eles voltam se você retornar a um tema com esses formatos.
                        </p>
                    </div>
                </Modal>
                <PageFooter spaceBetween={true}>
                    <Button
                        type={`primary`}
                        size={`small`}
                        svg={`save-white`}
                        text={`Salvar alterações`}
                        action={updateTenant}
                        loading={loading}
                    />                   
                </PageFooter>
            </PanelTemplate>
        </>
    );
}

export default MainTheme;
